import { API_BASE_URL, http } from "./http";
import type { EstablishmentPublic } from "./establishments";

type PublicFunnelEventPayload = {
  eventName:
    | "page_view"
    | "service_selected"
    | "slot_selected"
    | "form_started"
    | "checkout_started"
    | "appointment_created"
    /** OFR-19: o perfil abriu por um link de oferta do WhatsApp. */
    | "offer_opened";
  serviceId?: string | null;
  providerId?: string | null;
  scheduledDate?: string | null;
  scheduledTime?: string | null;
  metadata?: Record<string, unknown>;
  bookingSource?: string;
  bookingSourceDetail?: string | null;
  sessionId?: string;
};

declare global {
  interface Window {
    __agilizaPixelTrackingReady?: boolean;
    __agilizaPublicFunnelQueue?: Array<{
      slug: string;
      payload: PublicFunnelEventPayload;
    }>;
  }
}

export async function getPublicEstablishment(slug: string) {
  return http<EstablishmentPublic>(`/public/${slug}`);
}

export type PublicBranch = {
  id: string;
  name: string;
  slug: string;
  city?: string | null;
  state?: string | null;
  isCurrent?: boolean;
};

export async function getPublicSiblingEstablishments(
  slug: string
): Promise<PublicBranch[]> {
  const resp = await http<{
    currentSlug?: string;
    branches?: PublicBranch[];
  }>(`/public/${slug}/branches`);
  return Array.isArray(resp?.branches) ? resp.branches : [];
}

export type AvailabilityResponse = {
  slots: string[];
  /** Subconjunto de `slots` fora da grade regular: encaixes colados em outro atendimento. */
  fitSlots?: string[];
  reasonCode?: string;
  reason?: string;
  details?: any;
};

export async function getPublicAvailability(
  slug: string,
  date: string,
  serviceId?: string,
  providerId?: string,
  serviceIds?: string[]
): Promise<AvailabilityResponse> {
  const query: Record<string, string> = { date };
  if (serviceIds && serviceIds.length > 0) {
    query.serviceIds = serviceIds.join(",");
  } else if (serviceId) {
    query.serviceId = serviceId;
  }
  if (providerId) query.providerId = providerId;
  const resp = await http<any>(`/public/${slug}/availability`, { query });
  if (Array.isArray(resp)) {
    return { slots: resp };
  }
  if (resp && Array.isArray(resp.slots)) {
    return {
      slots: resp.slots as string[],
      fitSlots: Array.isArray(resp.fitSlots) ? (resp.fitSlots as string[]) : [],
      reasonCode: resp.reasonCode,
      reason: resp.reason,
      details: resp.details,
    };
  }
  return { slots: [] };
}

export async function getPublicWeeklyDays(slug: string, serviceId?: string) {
  const query: Record<string, string> = {};
  if (serviceId) query.serviceId = serviceId;
  return http<number[]>(`/public/${slug}/weekly-days`, { query });
}

export async function getPublicBlackoutDates(
  slug: string,
  from: string,
  to: string
): Promise<string[]> {
  const query: Record<string, string> = { from, to };
  return http<string[]>(`/public/${slug}/blackout-dates`, { query });
}

export type PublicUnitClosure = {
  date: string;
  publicMessage: string;
};

export async function getPublicUnitClosures(
  slug: string,
  from: string,
  to: string
): Promise<PublicUnitClosure[]> {
  const resp = await http<{ closures?: PublicUnitClosure[] }>(
    `/public/${slug}/unit-closures`,
    { query: { from, to } }
  );
  return Array.isArray(resp?.closures) ? resp.closures : [];
}

export type PublicService = {
  id: string;
  name: string;
  description?: string | null;
  durationMinutes: number;
  bufferBeforeMinutes?: number | null;
  bufferAfterMinutes?: number | null;
  imageUrl?: string | null;
  price?: number | null;
  showPrice?: boolean; // adicionado para alinhamento com uso na UI
  priceStartingFrom?: boolean;
  active: boolean;
  sourceEstablishmentId?: string;
  sourceEstablishmentName?: string;
  fromProviderAssignment?: boolean;
};

export async function getPublicServices(
  slug: string,
  options?: {
    date?: string;
    providerId?: string;
  }
) {
  const list = await http<any[]>(`/public/${slug}/services`, {
    query: {
      date: options?.date || undefined,
      providerId: options?.providerId || undefined,
    },
  });
  // Normaliza tipos vindos do backend (MySQL pode retornar DECIMAL como string)
  const normalized: PublicService[] = (Array.isArray(list) ? list : []).map((s: any) => ({
    id: String(s.id),
    name: String(s.name),
    description: s.description ? String(s.description) : null,
    durationMinutes: Number(s.durationMinutes || 0),
    bufferBeforeMinutes:
      s.bufferBeforeMinutes != null ? Number(s.bufferBeforeMinutes) : null,
    bufferAfterMinutes:
      s.bufferAfterMinutes != null ? Number(s.bufferAfterMinutes) : null,
    imageUrl: s.imageUrl ?? null,
    price: s.price != null && s.price !== "" ? Number(s.price) : null,
    showPrice: Boolean(s.showPrice),
    priceStartingFrom: Boolean(s.priceStartingFrom),
    active: Boolean(s.active),
    sourceEstablishmentId: s.sourceEstablishmentId
      ? String(s.sourceEstablishmentId)
      : undefined,
    sourceEstablishmentName: s.sourceEstablishmentName
      ? String(s.sourceEstablishmentName)
      : undefined,
    fromProviderAssignment: Boolean(s.fromProviderAssignment),
  }));
  return normalized;
}

export type PublicProvider = {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  averageRating?: number | null;
  reviewsCount?: number;
};
export async function getPublicProviders(
  slug: string,
  serviceId?: string,
  date?: string
) {
  const query: Record<string, string> = {};
  if (serviceId) query.serviceId = serviceId;
  if (date) query.date = date;
  return http<PublicProvider[]>(`/public/${slug}/providers`, { query });
}

export type PublicCustomField = {
  id: string;
  label: string;
  type: "text" | "textarea" | "select" | "checkbox";
  required: boolean;
  options?: string[];
};

export async function getPublicCustomFields(slug: string, serviceId?: string) {
  const query: Record<string, string> = {};
  if (serviceId) query.serviceId = serviceId;
  return http<PublicCustomField[]>(`/public/${slug}/custom-fields`, { query });
}

/**
 * CNF-03: o que conta como "veio do WhatsApp". Uma regra só para o site todo.
 *
 * Antes era qualquer `utm_source` que contivesse "wa", e "software", "twa" ou
 * "hawaii" viravam WhatsApp. A origem pesa no relatório e na confirmação que o
 * backend decide mandar, então ela precisa ser estreita: o valor exato
 * `whatsapp`, `wa` ou `wpp`, ou um valor que comece com `whatsapp_` ou
 * `whatsapp-` (como `whatsapp_bot`).
 */
const WHATSAPP_EXACT_SOURCES = new Set(["whatsapp", "wa", "wpp"]);
const WHATSAPP_SOURCE_PREFIXES = ["whatsapp_", "whatsapp-"];
const WHATSAPP_REFERRER_HOSTS = ["wa.me", "whatsapp.com"];

function isWhatsAppSource(normalized: string): boolean {
  return (
    WHATSAPP_EXACT_SOURCES.has(normalized) ||
    WHATSAPP_SOURCE_PREFIXES.some((prefix) => normalized.startsWith(prefix))
  );
}

// Pelo endereço do site, e não por pedaço do texto: "ottawa.media" contém "wa.me".
function isWhatsAppReferrer(referrer: string): boolean {
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return false;
  }
  return WHATSAPP_REFERRER_HOSTS.some(
    (site) => host === site || host.endsWith(`.${site}`)
  );
}

function withBookingSource<
  T extends { bookingSource?: string; bookingSourceDetail?: string },
>(payload: T): T {
  if (payload.bookingSource || typeof window === "undefined") return payload;
  const params = new URLSearchParams(window.location.search);
  const explicitSource = params.get("utm_source") || params.get("source") || "";
  const referrer = document.referrer || "";
  const normalized = explicitSource.trim().toLowerCase();
  if (normalized.includes("instagram")) {
    return {
      ...payload,
      bookingSource: "instagram",
      bookingSourceDetail: explicitSource,
    };
  }
  if (isWhatsAppSource(normalized)) {
    return { ...payload, bookingSource: "whatsapp", bookingSourceDetail: explicitSource };
  }
  if (normalized.includes("google")) {
    return { ...payload, bookingSource: "google", bookingSourceDetail: explicitSource };
  }
  if (explicitSource) {
    return { ...payload, bookingSource: "campaign", bookingSourceDetail: explicitSource };
  }
  if (referrer.includes("instagram")) return { ...payload, bookingSource: "instagram" };
  if (isWhatsAppReferrer(referrer)) {
    return { ...payload, bookingSource: "whatsapp" };
  }
  if (referrer.includes("google")) return { ...payload, bookingSource: "google" };
  return { ...payload, bookingSource: "public_link" };
}

export function resolvePublicBookingSource(): {
  bookingSource: string;
  bookingSourceDetail?: string;
} {
  const resolved = withBookingSource({}) as {
    bookingSource?: string;
    bookingSourceDetail?: string;
  };
  // Fora do navegador não há URL nem referrer para ler.
  return resolved.bookingSource
    ? (resolved as { bookingSource: string; bookingSourceDetail?: string })
    : { bookingSource: "public_link" };
}

export function getPublicFunnelSessionId(): string {
  if (typeof window === "undefined") return "server-session";
  const key = "agiliza:public-funnel-session-id";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const generated =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  sessionStorage.setItem(key, generated);
  return generated;
}

export function trackPublicFunnelEvent(slug: string, payload: PublicFunnelEventPayload) {
  if (!slug || typeof window === "undefined") return;
  const eventPayload = {
    ...resolvePublicBookingSource(),
    ...payload,
    sessionId: getPublicFunnelSessionId(),
  };
  if (!window.__agilizaPixelTrackingReady) {
    window.__agilizaPublicFunnelQueue = window.__agilizaPublicFunnelQueue || [];
    window.__agilizaPublicFunnelQueue.push({ slug, payload: eventPayload });
  }
  window.dispatchEvent(
    new CustomEvent("agiliza:public-funnel-event", {
      detail: { slug, payload: eventPayload },
    })
  );
  const body = JSON.stringify(eventPayload);
  const url = `${API_BASE_URL}/public/${encodeURIComponent(slug)}/funnel-events`;

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    // Ignora falhas de analytics para não afetar o agendamento.
  }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    credentials: "include",
    keepalive: true,
  }).catch(() => undefined);
}

/** Cobertura da assinatura serviço a serviço, calculada pelo servidor. */
export type PublicServiceCoverage = {
  serviceId: string;
  coveredByMembership: boolean;
  membershipCoverageReason: string | null;
  /** Preço cheio do serviço. */
  amountCents: number;
};

/**
 * Campos de cobertura da criação pública (MBR-10). Todos opcionais: resposta
 * de backend antigo não os traz, e o front mantém o comportamento anterior.
 */
export type PublicAppointmentCoverageFields = {
  coveredByMembership?: boolean;
  membershipRequiresPayment?: boolean;
  /** Soma dos serviços não cobertos: é o que PIX e cartão cobram. */
  amountDueCents?: number;
  totalAmountCents?: number;
  serviceCoverages?: PublicServiceCoverage[];
};

export async function createPublicAppointment(
  slug: string,
  payload: {
    name: string;
    email: string;
    whatsapp?: string;
    phone?: string;
    notes?: string;
    date: string; // YYYY-MM-DD
    time: string; // HH:mm
    serviceId?: string;
    serviceIds?: string[]; // multiplos servicos (quando allowMultiServices ativo)
    customFields?: Record<string, any>;
    providerId?: string;
    hold?: boolean;
    bookingSource?: string;
    bookingSourceDetail?: string;
  },
  token?: string
) {
  return http<
    {
      id: string;
      status?: "waiting_payment" | "confirmed";
      expiresAt?: string | null;
    } & PublicAppointmentCoverageFields
  >(`/public/${slug}/appointments`, {
    method: "POST",
    body: withBookingSource(payload),
    token,
  });
}

export type PublicAppointmentHold = {
  id: string;
  status: string;
  expiresAt?: string | null;
} & PublicAppointmentCoverageFields;

export async function createPublicAppointmentHold(
  slug: string,
  payload: Parameters<typeof createPublicAppointment>[1],
  token?: string
): Promise<PublicAppointmentHold> {
  return http<PublicAppointmentHold>(`/public/${slug}/appointments`, {
    method: "POST",
    body: withBookingSource({ ...payload, hold: true }),
    token,
  });
}

export type PublicProduct = {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  brand: string | null;
  imageUrl: string | null;
  description: string | null;
  salePriceCents: number;
  currentStock: number;
  unitLabel: string;
  available: boolean;
};

export async function getPublicProducts(slug: string): Promise<PublicProduct[]> {
  return http<PublicProduct[]>(`/public/${slug}/products`);
}

export async function createPublicProductOrder(
  slug: string,
  payload: {
    customerName: string;
    customerWhatsapp?: string | null;
    customerPhone?: string | null;
    customerEmail?: string | null;
    notes?: string | null;
    items: Array<{
      productId: string;
      quantity: number;
    }>;
  },
  token?: string | null
): Promise<{
  ok: boolean;
  order: {
    id: string;
    status: string;
    totalItems: number;
    totalCents: number;
    createdAt: string;
  };
}> {
  return http(`/public/${slug}/product-orders`, {
    method: "POST",
    body: payload,
    token: token || undefined,
  });
}
